// frontend/src/features/wind/WindFiltersPanel.js
import React from "react";

/**
 * Filters panel (region + capacity/vintage windows).
 */
export default function WindFiltersPanel({
  region,
  capacityMin,
  capacityMax,
  yearMin,
  yearMax,
  onRegionChange,
  onCapacityMinChange,
  onCapacityMaxChange,
  onYearMinChange,
  onYearMaxChange,
  onApply,
  loading,
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur shadow-[0_18px_60px_rgba(15,23,42,0.85)]">
      <div className="border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">
            Explore wind farms
          </h2>
          <p className="text-[11px] text-slate-400">
            Powered by the U.S. Wind Turbine Database (USWTDB). Use filters to
            define a window of projects, then build comp sets on the right.
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300 border border-emerald-500/40">
          v0.4 · Custom windows + map
        </span>
      </div>

      <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
        {/* Region */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-slate-200">
              Region
            </span>
          </div>
          <select
            value={region}
            onChange={(e) => onRegionChange(e.target.value)}
            className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-2.5 py-1.5 text-[11px] text-slate-100"
          >
            <option value="TX">Texas</option>
            <option value="OK">Oklahoma</option>
            <option value="US">US (all states)</option>
          </select>
          <div className="text-[10px] text-slate-500">
            We’ll add radius-based and multi-state regions next.
          </div>
        </div>

        {/* Capacity window */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-slate-200">
              Capacity window (MW)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={capacityMin}
              onChange={(e) => onCapacityMinChange(e.target.value)}
              className="w-1/2 rounded-xl border border-slate-700 bg-slate-950/80 px-2.5 py-1.5 text-[11px] text-slate-100"
              placeholder="Min"
            />
            <input
              type="number"
              min={0}
              value={capacityMax}
              onChange={(e) => onCapacityMaxChange(e.target.value)}
              className="w-1/2 rounded-xl border border-slate-700 bg-slate-950/80 px-2.5 py-1.5 text-[11px] text-slate-100"
              placeholder="Max (optional)"
            />
          </div>
          <div className="text-[10px] text-slate-500">
            Min MW is sent to the backend; max MW is applied client-side.
          </div>
        </div>

        {/* Vintage window */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-slate-200">
              Vintage window (year)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={yearMin}
              onChange={(e) => onYearMinChange(e.target.value)}
              className="w-1/2 rounded-xl border border-slate-700 bg-slate-950/80 px-2.5 py-1.5 text-[11px] text-slate-100"
              placeholder="Min year"
            />
            <input
              type="number"
              value={yearMax}
              onChange={(e) => onYearMaxChange(e.target.value)}
              className="w-1/2 rounded-xl border border-slate-700 bg-slate-950/80 px-2.5 py-1.5 text-[11px] text-slate-100"
              placeholder="Max year (optional)"
            />
          </div>
          <div className="text-[10px] text-slate-500">
            Min year is sent to the backend; max year is applied client-side.
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onApply}
          className="inline-flex items-center gap-1 rounded-xl border border-emerald-500/60 bg-emerald-900/20 px-3 py-1.5 text-[11px] text-emerald-100 disabled:opacity-40 hover:bg-emerald-900/40 transition"
          disabled={loading}
        >
          {loading ? "Loading…" : "Apply filters"}
        </button>
        <div className="text-[10px] text-slate-500">
          Filters → backend params → USWTDB → in-app windows.
        </div>
      </div>
    </div>
  );
}
